/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { Pressable, Text, View, type TextStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Close from '@move-travel/react-native-phoenix-icons/Close';
import { pd as tokens } from '@airasia-phoenix/react-native/tokens';
import {
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';

const CLOSE_ICON_SIZE = 24;
const CLOSE_ICON_COLOUR = getColor(tokens.sys.colour.content_primary);
const CLOSE_HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 };

/** Only the text keys of a Phoenix typescale, so it can go in a unistyles sheet. */
const textKeys = ({
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
  color,
}: TextStyle) => ({
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
  color,
});

export interface SheetHeaderProps {
  title?: string;
  /** Shows the close icon when provided. */
  onClosePress?: () => void;
  closeButtonAccessibilityLabel?: string;
  /** The sheet's test ID; inner IDs are derived from it. */
  testID: string;
}

/** Built-in sheet header: close icon on the left, title next to it. */
export const SheetHeader = ({
  title,
  onClosePress,
  closeButtonAccessibilityLabel,
  testID,
}: SheetHeaderProps) => (
  <View style={styles.header} testID={`${testID}-header`}>
    {onClosePress ? (
      <Pressable
        testID={`${testID}-close-button`}
        accessibilityRole="button"
        accessibilityLabel={closeButtonAccessibilityLabel}
        hitSlop={CLOSE_HIT_SLOP}
        onPress={onClosePress}
      >
        <Close size={CLOSE_ICON_SIZE} color={CLOSE_ICON_COLOUR} />
      </Pressable>
    ) : null}
    {title != null ? (
      <Text
        style={styles.title}
        numberOfLines={2}
        accessibilityRole="header"
        testID={`${testID}-title`}
      >
        {title}
      </Text>
    ) : null}
  </View>
);

const styles = StyleSheet.create((pd) => ({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: pd.ref.space.spacing20.px,
    paddingHorizontal: pd.ref.space.spacing40.px,
    paddingTop: pd.ref.space.spacing40.px,
  },
  title: {
    ...textKeys(
      getTextStyle(
        pd.sys.typescale.body_text_medium,
        pd.sys.colour.content_primary,
      ) as TextStyle,
    ),
    flex: 1,
  },
}));
